import { useState } from "react";
import { Search, Car, Loader2, RefreshCw, AlertCircle, CheckCircle2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { authFetch } from "../services/authService";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * VehicleLookup Component
 * 
 * Provides a license plate search field with a "Cerca" button.
 * On successful lookup, populates vehicle data via onVehicleFound callback.
 * Uses cache-first strategy with tuttotarghe.it API fallback.
 * 
 * @param {function} onVehicleFound - Callback with vehicle data when found
 * @param {string} initialPlate - Initial plate value (optional)
 * @param {boolean} showDetails - Show vehicle details after lookup (default: true)
 * @param {boolean} disabled - Disable the lookup (optional)
 */
export default function VehicleLookup({ 
  onVehicleFound, 
  initialPlate = "", 
  showDetails = true,
  disabled = false 
}) {
  const [plate, setPlate] = useState(initialPlate);
  const [loading, setLoading] = useState(false);
  const [vehicleData, setVehicleData] = useState(null);
  const [error, setError] = useState(null);
  const [searched, setSearched] = useState(false);

  const normalizePlate = (value) => {
    // Remove spaces, dashes, dots and convert to uppercase
    return value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
  };

  const handlePlateChange = (e) => {
    const normalized = normalizePlate(e.target.value);
    setPlate(normalized);
    // Reset previous search results when plate changes
    if (searched && normalized !== plate) {
      setSearched(false);
      setVehicleData(null);
      setError(null);
    }
  };

  const handleLookup = async (forceRefresh = false) => {
    if (!plate || plate.length < 5) {
      toast.error("Inserisci una targa valida (minimo 5 caratteri)");
      return;
    }

    setLoading(true);
    setError(null);
    setSearched(true);

    try {
      const url = `${API}/vehicles/lookup/${plate}${forceRefresh ? '?refresh=true' : ''}`;
      const response = await authFetch(url);

      if (response.ok) {
        const data = await response.json();
        setVehicleData(data);
        
        // Notify parent with found vehicle data
        if (onVehicleFound) {
          onVehicleFound(data);
        }
        
        const sourceText = data.source === 'cache' ? 'archivio locale' : 'TuttoTarghe API';
        toast.success(`Veicolo trovato (${sourceText})`);
      } else if (response.status === 404) {
        setVehicleData(null);
        setError("Veicolo non trovato nell'archivio");
        
        // Still notify parent with null/empty data
        if (onVehicleFound) {
          onVehicleFound(null);
        }
      } else {
        const errorData = await response.json();
        setError(errorData.detail || "Errore nella ricerca");
      }
    } catch (err) {
      setError("Errore di connessione");
      toast.error("Errore di connessione al server");
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleLookup();
    }
  };

  // Format fuel type for display
  const formatFuelType = (fuel) => {
    const fuelMap = {
      'benzina': 'Benzina',
      'diesel': 'Diesel',
      'gasolio': 'Diesel',
      'gpl': 'GPL',
      'metano': 'Metano',
      'elettrico': 'Elettrico',
      'ibrido': 'Ibrido',
      'ibrido_benzina': 'Ibrido Benzina',
      'ibrido_diesel': 'Ibrido Diesel'
    };
    return fuelMap[fuel?.toLowerCase()] || fuel || '-';
  };

  return (
    <div className="space-y-4">
      {/* Search Input */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Car className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input
            placeholder="Inserisci targa (es. AB123CD)"
            value={plate}
            onChange={handlePlateChange}
            onKeyDown={handleKeyDown}
            className="pl-10 bg-zinc-900 border-zinc-800 text-white uppercase font-mono tracking-wider"
            maxLength={10}
            disabled={disabled || loading}
            data-testid="vehicle-plate-input"
          />
        </div>
        <Button
          type="button"
          onClick={() => handleLookup(false)}
          disabled={disabled || loading || !plate || plate.length < 5}
          className="btn-primary min-w-[100px]"
          data-testid="vehicle-lookup-btn"
        >
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <>
              <Search className="w-4 h-4 mr-2" />
              Cerca
            </>
          )}
        </Button>
      </div>

      {/* Results */}
      {searched && showDetails && (
        <div className="mt-4">
          {error && !vehicleData && (
            <div className="flex items-center gap-2 p-4 bg-zinc-900/50 border border-zinc-800 rounded-lg text-zinc-400">
              <AlertCircle className="w-5 h-5 text-yellow-500" />
              <span>{error}</span>
              <span className="text-zinc-500 text-sm ml-2">
                Puoi inserire i dati manualmente
              </span>
            </div>
          )}

          {vehicleData && (
            <div className="p-4 bg-zinc-900/50 border border-primary/30 rounded-lg space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                  <span className="text-white font-medium">Veicolo trovato</span>
                  <span className="text-xs px-2 py-0.5 bg-zinc-800 rounded text-zinc-400">
                    {vehicleData.source === 'cache' ? 'Archivio locale' : 'TuttoTarghe'}
                  </span>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => handleLookup(true)}
                  disabled={loading}
                  className="text-zinc-400 hover:text-white"
                  title="Aggiorna da TuttoTarghe"
                >
                  <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                </Button>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-zinc-500 block">Targa</span>
                  <span className="text-white font-mono">{vehicleData.licensePlate}</span>
                </div>
                <div>
                  <span className="text-zinc-500 block">Marca</span>
                  <span className="text-white">{vehicleData.brand || '-'}</span>
                </div>
                <div>
                  <span className="text-zinc-500 block">Modello</span>
                  <span className="text-white">{vehicleData.model || '-'}</span>
                </div>
                <div>
                  <span className="text-zinc-500 block">Versione</span>
                  <span className="text-white">{vehicleData.modelDetail || '-'}</span>
                </div>
                <div>
                  <span className="text-zinc-500 block">Anno</span>
                  <span className="text-white">{vehicleData.buildYear || '-'}</span>
                </div>
                <div>
                  <span className="text-zinc-500 block">Alimentazione</span>
                  <span className="text-white">{formatFuelType(vehicleData.fuelType)}</span>
                </div>
                <div>
                  <span className="text-zinc-500 block">Potenza</span>
                  <span className="text-white">
                    {vehicleData.power_kw ? `${vehicleData.power_kw} kW` : '-'}
                    {vehicleData.power_hp ? ` (${vehicleData.power_hp} CV)` : ''}
                  </span>
                </div>
                <div>
                  <span className="text-zinc-500 block">Cilindrata</span>
                  <span className="text-white">
                    {vehicleData.cubicCapacity ? `${vehicleData.cubicCapacity} cc` : '-'}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
